import React, { useState, useRef, useEffect, useContext } from "react";
import {
  FiMaximize2,
  FiMinimize2,
  FiLogOut,
  FiUser,
  FiDroplet,
  FiSettings,
  FiBell,
  FiSearch,
  FiChevronRight,
  FiCommand,
  FiClock
} from "react-icons/fi";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { useNavigate } from "react-router-dom";

const HeaderBar = ({ title = "Dashboard" }) => {
  // Use ThemeContext instead of local state
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const navigate = useNavigate();
  
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const dropdownRef = useRef();
  const themeRef = useRef();
  const notificationRef = useRef();
  const searchRef = useRef();
  const searchResultsRef = useRef();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  // Route mapping from your sidebar
  const routeMap = {
    "Branch Items": "/branch-items",
    "Item Category": "/inventory/ItemCategory_Mas",
    "Branch Items Stock Update": "/branchitems-stockupdate",
    "Supplier Group Master": "/supplier-groupmaster",
    "Supplier Master": "/supplier-mainmaslist",
    "Supplier With Tax": "/supplier-withtax",
    "Supplier Items": "/supplier-withitem",
    "Supplier Mapping": '/supplier-mapping',
    "UOM Master": "/uom-master",
    "Department Master": "/department-master",
    "Transport Master": "/transpot-master",
    "Accounts Group": "/group-master",
    "Accounts Group Type Master": "/grouptype-master",
    "Ledger Master": "/ledger-master",
    "Store Master": "/store-master",
    "Employee Detail Master": "/employeedetail-listing",
    "Tally BP Header": "/tallyledgergroup-mas",
    "Purchase Requisition Listing": "/purchase-requisition-list",
    "Rate Comparision": "/quotation-list",
    "Purchase Order": "/purchaseorder-listing",
    "Open/Misc.. Purchase Order Listing": "/open/miscorder_listing",
    "Purchase Return": "/purchase-return",
    "Pending Purchase Request": "/pendingreq-listing",
    "Purchase Requisition Enquiry Listing": "/purchasereqentry-listing",
    "GRN Entry": "/grn-listing",
    "GRN Inspection Entry": "/grnins-listing",
    "GRN Confirmation Entry": "/grnconfirm-listing",
    "Bill Passing": "/billpassing-listing",
    "Store Receipt Note": "/storereceipt-listing",
    "Trye Retreded Issue": "/sctyreissue-listing",
    "STN Requsition": "/stnrequisition-listing",
    "Direct STN Outward": "/stnoutward",
    "STN Inward/Outward": "/stnout/in-listing",
    "STN Outward Approve": "/stnoutward-approve",
    "Store Issue Requsition": "/storeissuerequsition",
    "Store Issue Note": "/storeissuenote",
    "Vehicle Hsd Hour Updation": "/vehiclehsd-update",
    "Under Maintenance Entry": '/undermainspares_listing',
    "Purchase Requsition Reports": "/purchasereq-report",
    "Purchase Order Reports": "/purchaseorder-report",
    "Purchase Return Reports": "/purchasereturn-report",
    "Purchase Requsition Enquiry Reports": "/purchasereqenquiry-report",
    "Rate Comparison Reports": "/purchasereq-ratecomparison",
    "GRN Report": "/grn-report",
    "Ho BillPass Report": "/hobillpass-report",
    "STN Requsition Report": "/stnreq-report",
    "STN Inward Report": "/stninward-report",
    "STN Outward Report": "/stnoutward-report",
    "Store Requsition Report": "/storereq-report",
    "Store Issue Report": "/storeissue-report",
    "Under Maintenance Spares Report": "/umspare-report",
    "Under Maintenance Scrap Report": "/umscrap-report",
    "DeadStock Report": "/deadstock-report",
    "GST Report": "/gst-report",
    "TCS Report": "/tcs-report",
    "VAT Report": "/vat-report",
    "Vehicle HSD Filling Report": "/vehiclehsd-report",
    "Individual Stock Summary": "/individualstocksummary",
    "Stock Summary Report": "/stocksummary-report",
    "Ledger Report": "/ledger-report",
    "Tyre Serial No Report": "/tyreserialno-report",
    "CategoryWise Stock Report": "/categorywisestock-report",
    "Reorder Level Spares Report": "/reorderspares-report",
    "CST Report": "/cst-report",
    "Financial Year Closing-Adjustment Report": "/financialyear-report",
    "Branchwise Stock Summary": "/branchwise-report",
    "Fin Year Pending Transaction": "/finyearpending-report"
  };

  // All menu items from your sidebar - flattened for search
  const allMenuItems = [
    // Inventory Master
    { name: "Item Category", category: "Inventory Master > Item Code Generation", route: "/inventory/indentNoteView" },
    { name: "Branch Items", category: "Inventory Master > Item Code Generation", route: "/branch-items" },
    { name: "Branch Items Stock Update", category: "Inventory Master > Item Code Generation", route: "/branchitems-stockupdate" },
    { name: "Supplier Group Master", category: "Inventory Master > Supplier", route: "/supplier-groupmaster" },
    { name: "Supplier Master", category: "Inventory Master > Supplier", route: "/supplier-mainmaslist" },
    { name: "Supplier With Tax", category: "Inventory Master > Supplier", route: "/supplier-withtax" },
    { name: "Supplier Items", category: "Inventory Master > Supplier", route: "/supplier-withitem" },
    { name: "Supplier Mapping", category: "Inventory Master > Supplier", route: "/supplier-mapping" },
    { name: "UOM Master", category: "Inventory Master > Other Master", route: "/uom-master" },
    { name: "Department Master", category: "Inventory Master > Other Master", route: "/department-master" },
    { name: "Transport Master", category: "Inventory Master > Other Master", route: "/transpot-master" },
    { name: "Accounts Group", category: "Inventory Master > Other Master", route: "/group-master" },
    { name: "Accounts Group Type Master", category: "Inventory Master > Other Master", route: "/grouptype-master" },
    { name: "Ledger Master", category: "Inventory Master > Other Master", route: "/ledger-master" },
    { name: "Store Master", category: "Inventory Master > Other Master", route: "/store-master" },
    { name: "Employee Detail Master", category: "Inventory Master > Other Master", route: "/employeedetail-listing" },
    { name: "Tally BP Header", category: "Inventory Master > Other Master", route: "/tallyledgergroup-mas" },
    
    // Transaction
    { name: "Purchase Requisition Listing", category: "Transaction > Purchase Transaction", route: "/purchase-requisition-list" },
    { name: "Rate Comparision", category: "Transaction > Purchase Transaction", route: "/quotation-list" },
    { name: "Purchase Order", category: "Transaction > Purchase Transaction", route: "/purchaseorder-listing" },
    { name: "Open/Misc.. Purchase Order Listing", category: "Transaction > Purchase Transaction", route: "/open/miscorder_listing" },
    { name: "Purchase Return", category: "Transaction > Purchase Transaction", route: "/purchase-return" },
    { name: "Pending Purchase Request", category: "Transaction > Purchase Transaction", route: "/pendingreq-listing" },
    { name: "Purchase Requisition Enquiry Listing", category: "Transaction > Purchase Transaction", route: "/purchasereqentry-listing" },
    { name: "GRN Entry", category: "Transaction > GRN Transaction", route: "/grn-listing" },
    { name: "GRN Inspection Entry", category: "Transaction > GRN Transaction", route: "/grnins-listing" },
    { name: "GRN Confirmation Entry", category: "Transaction > GRN Transaction", route: "/grnconfirm-listing" },
    { name: "Bill Passing", category: "Transaction > Bill Passing", route: "/billpassing-listing" },
    { name: "Store Receipt Note", category: "Transaction > Store Receipt", route: "/storereceipt-listing" },
    { name: "Trye Retreded Issue", category: "Transaction > Sub Contract", route: "/sctyreissue-listing" },
    { name: "STN Requsition", category: "Transaction > STN Transactions", route: "/stnrequisition-listing" },
    { name: "Direct STN Outward", category: "Transaction > STN Transactions", route: "/stnoutward" },
    { name: "STN Inward/Outward", category: "Transaction > STN Transactions", route: "/stnout/in-listing" },
    { name: "STN Outward Approve", category: "Transaction > STN Transactions", route: "/stnoutward-approve" },
    { name: "Store Issue Requsition", category: "Transaction > Store Issue Transaction", route: "/storeissuerequsition" },
    { name: "Store Issue Note", category: "Transaction > Store Issue Transaction", route: "/storeissuenote" },
    { name: "Vehicle Hsd Hour Updation", category: "Transaction > Store Issue Transaction", route: "/vehiclehsd-update" },
    { name: "Under Maintenance Entry", category: "Transaction > Store Issue Transaction", route: "/undermainspares_listing" },
    
    // Inventory Report
    { name: "Purchase Requsition Reports", category: "Inventory Report > Purchase Reports", route: "/purchasereq-report" },
    { name: "Purchase Order Reports", category: "Inventory Report > Purchase Reports", route: "/purchaseorder-report" },
    { name: "Purchase Return Reports", category: "Inventory Report > Purchase Reports", route: "/purchasereturn-report" },
    { name: "Purchase Requsition Enquiry Reports", category: "Inventory Report > Purchase Reports", route: "/purchasereqenquiry-report" },
    { name: "Rate Comparison Reports", category: "Inventory Report > Purchase Reports", route: "/purchasereq-ratecomparison" },
    { name: "GRN Report", category: "Inventory Report > GRN Reports", route: "/grn-report" },
    { name: "Ho BillPass Report", category: "Inventory Report > GRN Reports", route: "/hobillpass-report" },
    { name: "STN Requsition Report", category: "Inventory Report > STN Reports", route: "/stnreq-report" },
    { name: "STN Inward Report", category: "Inventory Report > STN Reports", route: "/stninward-report" },
    { name: "STN Outward Report", category: "Inventory Report > STN Reports", route: "/stnoutward-report" },
    { name: "Store Requsition Report", category: "Inventory Report > Store Issue Reports", route: "/storereq-report" },
    { name: "Store Issue Report", category: "Inventory Report > Store Issue Reports", route: "/storeissue-report" },
    { name: "Under Maintenance Spares Report", category: "Inventory Report > Store Issue Reports", route: "/umspare-report" },
    { name: "Under Maintenance Scrap Report", category: "Inventory Report > Store Issue Reports", route: "/umscrap-report" },
    { name: "DeadStock Report", category: "Inventory Report > Other Reports", route: "/deadstock-report" },
    { name: "GST Report", category: "Inventory Report > Other Reports", route: "/gst-report" },
    { name: "TCS Report", category: "Inventory Report > Other Reports", route: "/tcs-report" },
    { name: "VAT Report", category: "Inventory Report > Other Reports", route: "/vat-report" },
    { name: "Vehicle HSD Filling Report", category: "Inventory Report > Other Reports", route: "/vehiclehsd-report" },
    { name: "Individual Stock Summary", category: "Inventory Report > Other Reports", route: "/individualstocksummary" },
    { name: "Stock Summary Report", category: "Inventory Report > Other Reports", route: "/stocksummary-report" },
    { name: "Ledger Report", category: "Inventory Report > Other Reports", route: "/ledger-report" },
    { name: "Tyre Serial No Report", category: "Inventory Report > Other Reports", route: "/tyreserialno-report" },
    { name: "CategoryWise Stock Report", category: "Inventory Report > Other Reports", route: "/categorywisestock-report" },
    { name: "Reorder Level Spares Report", category: "Inventory Report > Other Reports", route: "/reorderspares-report" },
    { name: "CST Report", category: "Inventory Report > Other Reports", route: "/cst-report" },
    { name: "Financial Year Closing-Adjustment Report", category: "Inventory Report > Other Reports", route: "/financialyear-report" },
    { name: "Branchwise Stock Summary", category: "Inventory Report > Other Reports", route: "/branchwise-report" },
    { name: "Fin Year Pending Transaction", category: "Inventory Report > Other Reports", route: "/finyearpending-report" }
  ];

  // Define available themes/skins
  const availableThemes = [
    { name: "emerald", color: "bg-emerald-600", label: "Emerald", preview: "#10b981" },
    { name: "blue", color: "bg-blue-600", label: "Blue", preview: "#2563eb" },
    { name: "purple", color: "bg-purple-600", label: "Purple", preview: "#9333ea" },
    { name: "green", color: "bg-green-600", label: "Green", preview: "#16a34a" },
    { name: "orange", color: "bg-orange-600", label: "Orange", preview: "#ea580c" },
  ];

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedSearchIndex(-1);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const filteredResults = allMenuItems.filter(item => 
      item.name.toLowerCase().includes(query.toLowerCase()) ||
      item.category.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 8); // Limit to 8 results

    setSearchResults(filteredResults);
  };

  const handleSearchNavigation = (item) => {
    if (item.route) {
      navigate(item.route);
      setSearchQuery("");
      setSearchResults([]);
      setIsSearchFocused(false);
      searchRef.current?.blur();
    }
  };

  const handleKeyDown = (e) => {
    if (!isSearchFocused || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSearchIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSearchIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSearchIndex >= 0 && searchResults[selectedSearchIndex]) {
          handleSearchNavigation(searchResults[selectedSearchIndex]);
        } else if (searchResults.length > 0) {
          handleSearchNavigation(searchResults[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsSearchFocused(false);
        setSearchQuery("");
        setSearchResults([]);
        setSelectedSearchIndex(-1);
        searchRef.current?.blur();
        break;
    }
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullScreen(!isFullScreen);
  };

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(event.target)) {
        setThemeOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target) && 
          searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
        setSelectedSearchIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchFocused, searchResults, selectedSearchIndex]);

  // Handle escape key to close dropdowns
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        setThemeOpen(false);
        setNotificationOpen(false);
        setIsSearchFocused(false);
        setSearchResults([]);
        setSelectedSearchIndex(-1);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  const notifications = [
    { id: 1, title: "New message received", time: "2 min ago", unread: true },
    { id: 2, title: "Server maintenance scheduled", time: "1 hour ago", unread: true },
    { id: 3, title: "Backup completed successfully", time: "3 hours ago", unread: false },
    { id: 4, title: "System update available", time: "5 hours ago", unread: false },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleThemeChange = (themeName) => {
    try {
      setSkin(themeName);
      setThemeOpen(false);
      console.log(`Theme changed to: ${themeName}`);
    } catch (error) {
      console.error('Error changing theme:', error);
    }
  };

  const handleLogout = () => {
    try {
      window.location.replace("/login");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/login");
    }
  };

  return (
    <div className="flex items-center justify-between mb-6 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border border-gray-200/50 dark:border-gray-700/50 rounded-2xl px-6 py-4 shadow-lg relative z-50">
      {/* Left Section - Title and Search */}
      <div className="flex items-center space-x-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            {title}
          </h1>
          <div className="flex items-center space-x-3 mt-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
              <FiClock className="w-3 h-3" />
              <span>
                {currentTime.toLocaleTimeString('en-US', { 
                  hour12: true,
                  hour: 'numeric',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>
        
        {/* Enhanced Search Bar */}
        <div className="hidden md:flex relative">
          <div className={`flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-2 min-w-[320px] transition-all duration-200 ${
            isSearchFocused ? 'ring-2 ring-blue-500/20 bg-white dark:bg-gray-700 shadow-lg' : 'focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:bg-white dark:focus-within:bg-gray-700'
          }`}>
            <FiSearch className="w-4 h-4 text-gray-400 mr-3" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search menus and features... (Ctrl+K)"
              className="bg-transparent border-none outline-none flex-1 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
            />
            <div className="flex items-center gap-1 text-xs text-gray-400 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5">
              <FiCommand className="w-3 h-3" />
              <span>K</span>
            </div>
          </div>
          
          {/* Search Results Dropdown */}
          {isSearchFocused && (searchResults.length > 0 || searchQuery.trim()) && (
            <div 
              ref={searchResultsRef}
              className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[99999] overflow-hidden max-h-96"
            >
              {searchResults.length > 0 ? (
                <>
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      Search Results ({searchResults.length})
                    </p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {searchResults.map((item, index) => (
                      <div
                        key={`${item.name}-${index}`}
                        className={`px-4 py-3 cursor-pointer transition-all duration-150 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                          selectedSearchIndex === index 
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        onClick={() => handleSearchNavigation(item)}
                        onMouseEnter={() => setSelectedSearchIndex(index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              selectedSearchIndex === index 
                                ? 'text-blue-900 dark:text-blue-100' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {item.name}
                            </p>
                            <p className={`text-xs truncate mt-1 ${
                              selectedSearchIndex === index 
                                ? 'text-blue-600 dark:text-blue-300' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {item.category}
                            </p>
                          </div>
                          <FiChevronRight className={`w-4 h-4 ml-2 flex-shrink-0 ${
                            selectedSearchIndex === index 
                              ? 'text-blue-600 dark:text-blue-400' 
                              : 'text-gray-400 dark:text-gray-500'
                          }`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : searchQuery.trim() ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No results found for "{searchQuery}"
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Try searching for menu names or categories
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Actions */}
      <div className="flex items-center space-x-3">
        {/* Fullscreen Toggle */}
        <button 
          onClick={toggleFullScreen} 
          className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105 group"
          title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullScreen ? (
            <FiMinimize2 className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-white transition-colors" />
          ) : (
            <FiMaximize2 className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-white transition-colors" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative z-50" ref={notificationRef}>
          <button 
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105 group relative"
            title="Notifications"
          >
            <FiBell className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-white transition-colors" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>
          
          {notificationOpen && (
            <div className="fixed right-4 top-20 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-[99999] overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <h3 className="font-semibold text-lg">Notifications</h3>
                <p className="text-blue-100 text-sm">{unreadCount} unread messages</p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className={`px-6 py-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors cursor-pointer ${
                      notification.unread ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {notification.title}
                      </p>
                      {notification.unread && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {notification.time}
                    </p>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 text-center border-t border-gray-200 dark:border-gray-700">
                <button className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline">
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Theme Skin Picker */}
        <div className="relative z-50" ref={themeRef}>
          <button 
            onClick={() => setThemeOpen(!themeOpen)}
            className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105 group"
            title="Theme Skins"
          >
            <FiDroplet className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-white transition-colors" />
          </button>
          
          {themeOpen && (
            <div className="fixed right-4 top-20 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-[99999] overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Choose Theme</h3>
                    <p className="text-purple-100 text-sm">Personalize your experience</p>
                  </div>
                  <button
                    onClick={() => setThemeOpen(false)}
                    className="text-white/80 hover:text-white text-xl transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="p-3 max-h-80 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {availableThemes.map((theme, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleThemeChange(theme.name)}
                      className={`p-3 text-sm flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 ${
                        skin === theme.name ? "bg-gray-100 dark:bg-gray-700 ring-2 ring-blue-500" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span
                          className={`w-5 h-5 rounded-full ${theme.color} shadow-md transition-all duration-200 ${
                            skin === theme.name ? "ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800" : ""
                          }`}
                        ></span>
                        <span className="text-xs font-medium">{theme.label}</span>
                        {skin === theme.name && (
                          <span className="ml-auto text-blue-500 text-sm">✓</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Current: <span className="font-medium capitalize">{skin}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div className="relative z-50" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2.5 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 hover:scale-105 shadow-lg"
          >
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FiUser className="w-4 h-4" />
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-semibold">Abhi</p>
              <p className="text-xs text-blue-100">Administrator</p>
            </div>
          </button>
          
          {dropdownOpen && (
            <div className="fixed right-4 top-20 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-[99999] overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FiUser className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Abhi</h3>
                    <p className="text-blue-100 text-sm">admin@company.com</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <button className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200">
                  <FiUser className="mr-3 w-4 h-4" /> 
                  <span>View Profile</span>
                </button>
                <button className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200">
                  <FiSettings className="mr-3 w-4 h-4" /> 
                  <span>Account Settings</span>
                </button>
                <hr className="my-2 border-gray-200 dark:border-gray-700" />
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all duration-200"
                >
                  <FiLogOut className="mr-3 w-4 h-4" /> 
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Keyboard Shortcut for Search */}
      <style jsx>{`
        /* Custom scrollbar for search results */
        .search-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.5) rgba(243, 244, 246, 0.5);
        }
        
        .search-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .search-scrollbar::-webkit-scrollbar-track {
          background: rgba(243, 244, 246, 0.5);
          border-radius: 3px;
        }
        
        .search-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        
        .search-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.7);
        }

        .dark .search-scrollbar {
          scrollbar-color: rgba(107, 114, 128, 0.5) rgba(55, 65, 81, 0.5);
        }
        
        .dark .search-scrollbar::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.5);
        }
        
        .dark .search-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(107, 114, 128, 0.5);
        }
        
        .dark .search-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 0.7);
        }
      `}</style>
    </div>
  );
};

// Add global keyboard shortcut effect
const GlobalKeyboardShortcut = () => {
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Ctrl+K or Cmd+K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search menus"]');
        if (searchInput) {
          searchInput.focus();
          // Trigger the focus event to show search dropdown
          searchInput.dispatchEvent(new Event('focus', { bubbles: true }));
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  return null;
};

// Export both components
export { GlobalKeyboardShortcut };
export default HeaderBar;